import { AsyncPipe, NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ENVIRONMENT_CONFIGURATION } from '@softwarehaus/util-angular-configuration';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { NappEnvironmentConfiguration } from '~/app/config/environments-configuration';
import { ProfileImageComponent } from '~/app/core/account/profile/profile-image.component';
import { LoginComponent } from '~/app/core/auth/login/login.component';
import { RegistrationComponent } from '~/app/core/auth/registration/registration.component';
import { ResetPasswordComponent } from '~/app/core/auth/reset-password/reset-password.component';
import { JSON } from '~/app/core/JSON.interface';
import { DamageReportComponent } from '~/app/shared/components/damage-report.component';
import { FeedbackComponent } from '~/app/shared/components/feedback.component';
import { PopupComponent } from '~/app/shared/components/popup.component';
import { getTimezoneOffsetInValidPattern } from '~/app/shared/helpers/timezone';
import { AppAngularBridge } from '../../app-bridge';
import { StorageConfig } from '../../config/storage.config';
import { FeedService, FeedTypes } from '../../features/feed/services/feed.service';
import { Profile } from '../account/profile/models/profile';
import { ProfileConfig } from '../account/profile/models/profile-config';
import { ProfileConfigService } from '../account/profile/services/profile-config.service';
import { ProfileService } from '../account/profile/services/profile.service';
import { AuthService } from '../auth/services/authentication.service';
import { PermissionService } from '../auth/services/permission.service';
import { ReportContentService } from '../services/backend/report-content.service';
import { MessageService, TOASTTYPE } from '../services/message.service';
import { LayoutService } from '../services/store/layout.service';
import { AppTranslationService } from '../services/translation/app-translation.service';

@Component({
  imports: [
    DxButtonModule,
    DxLoadIndicatorModule,
    NgClass,
    TranslateModule,
    AsyncPipe,
    ResetPasswordComponent,
    RegistrationComponent,
    LoginComponent,
    RouterLink,
    DxSelectBoxModule,
    ProfileImageComponent,
    PopupComponent,
    DamageReportComponent,
    FeedbackComponent,
    RouterLinkActive,
  ],
  selector: 'app-sidebar-user',
  templateUrl: './sidebar-user.component.html',
})
export class SidebarUserComponent implements OnInit, OnDestroy {
  public profileService = inject(ProfileService);
  public profile: Profile;

  public languages: string[] = [];
  public selectedLanguage = this.languages[0];
  public langMenuOpened = false;
  public feedTypes = FeedTypes;
  public isLoggedIn = false;
  public open = false;
  public notApprovedFeedPostCount = 0;
  public contentReportsCount = 0;
  public analyticsUrl?: string;
  public showApprovedEntry: boolean;
  public canViewNonApprovedEntries = false;
  public showContentReportEntry: boolean;
  public canViewContentReports = false;

  private layoutSubscription?: Subscription;
  private authSubscription?: Subscription;
  private configSubscription?: Subscription;
  private langStoredSubscription?: Subscription;
  private translationSubscription?: Subscription;

  public authService = inject(AuthService);
  public layoutService = inject(LayoutService);
  public permissionService = inject(PermissionService);
  public appTranslationService = inject(AppTranslationService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private profileConfigService = inject(ProfileConfigService);
  private feedService = inject(FeedService);
  private reportContentService = inject(ReportContentService);
  private config = inject<NappEnvironmentConfiguration>(ENVIRONMENT_CONFIGURATION);

  constructor() {
    this.analyticsUrl = this.config.analyticsUrl;
    this.showApprovedEntry = this.config.availableFeatures.feed.approve;
    this.showContentReportEntry = this.config.availableFeatures.feed.report;
  }

  public ngOnInit() {
    this.langStoredSubscription = this.appTranslationService.hasLanguagesStored
      .pipe(filter((value) => value))
      .subscribe(() => {
        this.setUpLangs();
      });
    if (this.appTranslationService.hasStoredLanguages()) {
      this.setUpLangs();
    }

    this.updateCapabilities();

    this.layoutSubscription = this.layoutService.showUserSidebarSubject.subscribe((next) => {
      this.open = next;
      if (this.open) {
        setTimeout(() => {
          this.authService
            .isLoggedIn()
            .pipe(take(1))
            .subscribe({
              next: (isLoggedIn) => {
                if (isLoggedIn) {
                  this.profileService.fetchAll({ expansions: ['profilePicture'] }).subscribe((results) => {
                    if (results && results.length === 1) {
                      this.profile = results[0];
                    }
                  });
                  this.getNotApprovedFeedPostCount();
                  this.getContentReportsCount();
                }
              },
            });
        }, 0);
      } else {
        this.langMenuOpened = this.open;
      }
    });
    this.authSubscription = this.authService
      .isLoggedIn()
      .pipe(distinctUntilChanged())
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        this.adjustLoginStatus();
      });
  }

  public ngOnDestroy() {
    this.layoutSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.translationSubscription?.unsubscribe();
    this.langStoredSubscription?.unsubscribe();
    this.configSubscription?.unsubscribe();
  }

  public logout() {
    this.authService.logout().subscribe({
      next: () => {
        this.messageService.showToast(TOASTTYPE.SUCCESS, this.translate.instant('logout.success'));
        this.close();
      },
    });
  }

  public close() {
    this.authService.resetPasswordUser = false;
    this.authService.registrateUser = false;
    this.layoutService.toggleUserSidebar(false);
    this.layoutService.scrollHandling();
  }

  public changePushSetting(status: boolean) {
    let pushChangeObservable: Observable<boolean> | boolean;
    if (status) {
      pushChangeObservable = (AppAngularBridge.enablePush() as Observable<boolean>) ?? false;
    } else {
      pushChangeObservable = (AppAngularBridge.disablePush() as Observable<boolean>) ?? false;
    }
    if (pushChangeObservable) {
      pushChangeObservable.subscribe((value) => {
        this.authService.pushStatus = value;
      });
    }
  }

  public feedback() {
    this.layoutService.isFeedbackShown.next(true);
  }

  public damageReport() {
    this.layoutService.isDamageReportShown.next(true);
  }

  public onLanguageSelect(event: ValueChangedEvent) {
    this.appTranslationService.setAppTranslation(event.value.toLowerCase()).subscribe(() => {
      this.messageService.showToast(TOASTTYPE.SUCCESS, this.translate.instant('general.langs.changed'));
      this.layoutService.triggerLeftMenuReload.next(true);
    });
  }

  public displayUser(): string {
    if (
      localStorage.getItem(StorageConfig.USER_FIRSTNAME) === 'undefined' &&
      localStorage.getItem(StorageConfig.USER_FIRSTNAME) !== null
    ) {
      return '';
    }
    return localStorage.getItem(StorageConfig.USER_FIRSTNAME) + ' ' + localStorage.getItem(StorageConfig.USER_LASTNAME);
  }

  public isNative() {
    return AppAngularBridge.isNative();
  }

  private checkPushStatus() {
    const pushStatusObservable = AppAngularBridge.getPushStatus();
    if (pushStatusObservable) {
      pushStatusObservable.subscribe((value) => (this.authService.pushStatus = value ?? false));
    }
  }

  private setUpLangs() {
    this.languages = [];
    this.appTranslationService.getStoredLanguages().forEach((items) => {
      if (items.active) {
        this.languages.push(items.iso.toUpperCase());
      }
    });

    const lang = this.appTranslationService.getCurrentLanguage();
    if (lang) {
      this.selectedLanguage = lang.iso.toUpperCase();
    } else {
      this.selectedLanguage = this.languages[0];
    }

    if (!this.translationSubscription) {
      this.translationSubscription = this.appTranslationService.languageChanged.subscribe(() => {
        this.selectedLanguage = this.appTranslationService.getCurrentLanguage().iso.toUpperCase();
      });
    }
  }

  private adjustLoginStatus() {
    this.updateCapabilities();

    if (this.isLoggedIn) {
      this.getNotApprovedFeedPostCount();
      this.getContentReportsCount();

      if (AppAngularBridge.isNative()) {
        this.checkPushStatus();
      }
      this.layoutService.toggleUserSidebar(false);
      this.layoutService.toggleLeftSidebar(false);

      const profileConfigLoaded = (userConfig: ProfileConfig[]) => {
        this.configSubscription?.unsubscribe();
        if (userConfig.length > 0) {
          const userSetLangId = userConfig[0].language?.id;
          if (userSetLangId) {
            const storedLang = this.appTranslationService.getLanguageById(userSetLangId);
            let source: Observable<JSON>;
            if (storedLang) {
              source = this.appTranslationService.setAppTranslation(storedLang.iso);
            } else {
              source = this.appTranslationService.setAppTranslationUserPreferredLang();
            }
            source.pipe(take(1)).subscribe();
          }
        }
      };

      this.configSubscription = this.profileConfigService.fetchAll().subscribe({
        next: profileConfigLoaded,
        error: (error) => {
          this.configSubscription?.unsubscribe();
          if (error?.error?.code === 'userconfig_not_found') {
            const profileConfig = new ProfileConfig();
            profileConfig.timezone = getTimezoneOffsetInValidPattern();
            profileConfig.language = this.appTranslationService.getCurrentLanguage();
            this.configSubscription = this.profileConfigService
              .post(profileConfig)
              .subscribe((result) => profileConfigLoaded([result]));
          }
        },
      });
    }
  }

  private updateCapabilities() {
    this.canViewNonApprovedEntries =
      this.isLoggedIn &&
      this.showApprovedEntry &&
      this.permissionService.checkScopes(['mgmt.feedpost.approve']) &&
      this.permissionService.checkScopes(['mgmt.feedpost.update']);

    this.canViewContentReports =
      this.isLoggedIn &&
      this.showContentReportEntry &&
      this.permissionService.checkScopes(['mgmt.reportcontent.read']) &&
      this.permissionService.checkScopes(['mgmt.reportcontent.update']);
  }

  private getNotApprovedFeedPostCount() {
    if (!this.canViewNonApprovedEntries) {
      return;
    }

    this.feedService.getNotApprovedCount().subscribe((data) => {
      this.notApprovedFeedPostCount = data.count;
    });
  }

  private getContentReportsCount() {
    if (!this.canViewContentReports) {
      return;
    }

    this.reportContentService.getContentReportCount().subscribe((data) => {
      this.contentReportsCount = data.count;
    });
  }
}
